import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@ui/card.tsx';
import { Switch } from '@ui/switch.tsx';
import { Label } from '@ui/label.tsx';
import { toast } from 'sonner';
import { unparse } from 'papaparse';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@ui/dialog.tsx';
import { Button } from '@ui/button.tsx';

import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { Download } from 'lucide-react';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { useProductReferenceColumns } from '@admin/components/performances/products-references-columns.tsx';
import { downloadCSV } from '@r/utils/utils.ts';
import {
  apiReferencesGetCollection,
  adminWorkspaceConfigs,
  apiAdminworkspaceConfigsIdPatch,
  getApiReferencesGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import { useAllConfigs, findConfig, getConfigsFromResponse } from '@r/hooks/use-all-configs.ts';
import { useGetConfigByType } from '@r/hooks/use-workspace-config.ts';
import { FileUploader } from '@r/components/import/file-uploader.tsx';
import { CsvFile } from '@r/components/import/csv-file.tsx';
import { ImportErrorPage } from '@r/components/import/import-error-page.tsx';
import { usePutFileParserWithErrors } from '@r/hooks/use-file-parser-put-with-errors.ts';
import { useImportState } from '@r/hooks/use-import-state.ts';
import { useWorkspaceName } from '@r/hooks/use-workspace-name.ts';
import { useProductReferenceEnabled } from '@r/hooks/use-product-reference-enabled.ts';
import { useUser } from '@r/api/auth.tsx';
import { getAdminTablePersistenceKey } from '@r/utils/data-table-persistence.ts';
import { z } from 'zod';
import type {
  ReferenceReferenceReadModel,
  WorkspaceConfigWorkspaceConfigReadModel
} from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useHideableWorkspaceConfigSwitch } from '@admin/hooks/use-hideable-workspace-config-switch.ts';
import { fetchAllReferences, REFERENCES_MAX_PAGE_SIZE } from '@r/hooks/use-all-references.ts';

const CONFIG_TYPE_CORE = 'core';
const CONFIG_KEY_IS_REFERENCE_ENABLED = 'is-reference-enabled';
const CONFIG_KEY_HIDE_REFERENCE = 'hide-reference';
const CONFIG_KEY_HIDE_REFERENCE_POINTS = 'hide-reference-points';
import type { DataTableFilterField } from '@r/types/index.ts';

const REFERENCES_PAGE_SIZE_OPTIONS = [10, 20, REFERENCES_MAX_PAGE_SIZE];
const PRODUCT_REFERENCE_FILTER_FIELDS: DataTableFilterField<ReferenceReferenceReadModel>[] = [];

export function ProductsReferences() {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const user = useUser();
  const [isExporting, setIsExporting] = useState(false);
  const { sanitizedWorkspaceName } = useWorkspaceName();
  const isReferenceEnabled = useProductReferenceEnabled();
  const queryClient = useQueryClient();

  const [configId, setConfigId] = useState<number | null>(null);
  // app.tsx bloque le rendu tant que le workspace config n'est pas chargé (app.tsx:21-23),
  // donc isReferenceEnabled reflète déjà la vraie valeur au tout premier render : on évite
  // ainsi le "saut" visuel OFF→ON du switch le temps que la cascade configId/hasReferences
  // se résolve.
  const [switchState, setSwitchState] = useState(isReferenceEnabled);
  const [isUpdatingConfig, setIsUpdatingConfig] = useState(false);
  const [showActivationDialog, setShowActivationDialog] = useState(false);
  const [apiPagination, setApiPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const hideReferenceRaw = useGetConfigByType(CONFIG_TYPE_CORE, CONFIG_KEY_HIDE_REFERENCE);
  const isReferenceHidden = hideReferenceRaw?.[0] === 'true';
  const hideReferencePointsRaw = useGetConfigByType(
    CONFIG_TYPE_CORE,
    CONFIG_KEY_HIDE_REFERENCE_POINTS
  );
  const isReferencePointsHidden = hideReferencePointsRaw?.[0] === 'true';

  const { state, setErrorState, setPreviewState, resetState } = useImportState('references');

  // Orval génère un wrapper { data: [...] } pour l'import; le parser attend le schéma d’une ligne.
  const referenceImportRowSchema = z
    .object({
      brand: z.string().max(100),
      productLine: z.string().max(100).nullish(),
      name: z.string().max(100),
      uniqueCode: z.string().max(50),
      pointValue: z.number().min(0),
      tags: z.array(z.string()).optional()
    })
    .passthrough();

  const { onParse } = usePutFileParserWithErrors(
    referenceImportRowSchema,
    '/admin/references/import'
  );

  const referencesParams = {
    page: apiPagination.pageIndex + 1,
    perPage: Math.min(apiPagination.pageSize, REFERENCES_MAX_PAGE_SIZE)
  };

  async function referencesListQuery(params: typeof referencesParams) {
    try {
      return await apiReferencesGetCollection(params);
    } catch (error) {
      if (isOrvalErrorFromPath(error, 'get', '/references')) {
        showOrvalErrorToast(error, t('toast.error.label'));
      } else {
        toast.error(t('toast.error.label'));
      }
      throw error;
    }
  }

  const referencesQuery = useQuery({
    queryKey: getApiReferencesGetCollectionQueryKey(referencesParams),
    queryFn: () => referencesListQuery(referencesParams),
    // Garde la page précédente pendant le changement de page : évite que hasReferences
    // repasse à false (et le flash du skeleton) le temps du fetch.
    placeholderData: keepPreviousData
  });

  const references = useMemo(() => referencesQuery.data?.data ?? [], [referencesQuery.data]);
  const referencesPagination = referencesQuery.data?.pagination;
  // Total serveur (toutes pages) : la page courante seule ne dit pas si le référentiel est vide.
  const hasReferences = (referencesPagination?.totalItems ?? 0) > 0;

  const productsReferencesTablePersistenceKey = useMemo(
    // Cle de persistance isolee par utilisateur pour conserver ses preferences.
    () => getAdminTablePersistenceKey('products-references', user.data?.id),
    [user.data?.id]
  );

  // Fetch de toutes les configs disponibles (mutualisé)
  const configsQuery = useAllConfigs();
  const availableConfigs = getConfigsFromResponse(configsQuery.data);

  const isReferenceEnabledConfig = findConfig(
    availableConfigs,
    CONFIG_TYPE_CORE,
    CONFIG_KEY_IS_REFERENCE_ENABLED
  );
  const isReferenceEnabledConfigIri = isReferenceEnabledConfig?.id
    ? `/configs/${isReferenceEnabledConfig.id}`
    : null;

  const hideReferenceConfigDef = findConfig(
    availableConfigs,
    CONFIG_TYPE_CORE,
    CONFIG_KEY_HIDE_REFERENCE
  );
  const hideReferenceConfigIri = hideReferenceConfigDef?.id
    ? `/configs/${hideReferenceConfigDef.id}`
    : null;

  const hideReferencePointsConfigDef = findConfig(
    availableConfigs,
    CONFIG_TYPE_CORE,
    CONFIG_KEY_HIDE_REFERENCE_POINTS
  );
  const hideReferencePointsConfigIri = hideReferencePointsConfigDef?.id
    ? `/configs/${hideReferencePointsConfigDef.id}`
    : null;

  // Fetch des workspace configs (is-reference-enabled / hide-reference / hide-reference-points).
  // Filtre serveur explicite : la collection admin est paginée (30 max sans filtre), sans ça
  // les 3 clés ciblées peuvent être hors de la première page.
  const workspaceConfigsQuery = useQuery<WorkspaceConfigWorkspaceConfigReadModel[]>({
    queryKey: ['workspaceConfigs', CONFIG_KEY_IS_REFERENCE_ENABLED],
    queryFn: async () => {
      const response = await adminWorkspaceConfigs({
        'config.type': CONFIG_TYPE_CORE,
        'config.key[]': [
          CONFIG_KEY_IS_REFERENCE_ENABLED,
          CONFIG_KEY_HIDE_REFERENCE,
          CONFIG_KEY_HIDE_REFERENCE_POINTS
        ]
      });
      return (response.data || []) as WorkspaceConfigWorkspaceConfigReadModel[];
    },
    enabled: !!isReferenceEnabledConfigIri
  });

  const findWorkspaceConfigId = useCallback(
    (key: string) =>
      workspaceConfigsQuery.data?.find(
        (wc) => wc.config?.type === CONFIG_TYPE_CORE && wc.config?.key === key
      )?.id,
    [workspaceConfigsQuery.data]
  );

  const hideReferenceSwitch = useHideableWorkspaceConfigSwitch({
    configType: CONFIG_TYPE_CORE,
    configKey: CONFIG_KEY_HIDE_REFERENCE,
    configIri: hideReferenceConfigIri,
    workspaceConfigsData: workspaceConfigsQuery.data,
    isDiscoveryLoading: workspaceConfigsQuery.isLoading,
    currentValue: isReferenceHidden,
    queryClient,
    t,
    showOrvalErrorToast
  });

  const hideAmountSwitch = useHideableWorkspaceConfigSwitch({
    configType: CONFIG_TYPE_CORE,
    configKey: CONFIG_KEY_HIDE_REFERENCE_POINTS,
    configIri: hideReferencePointsConfigIri,
    workspaceConfigsData: workspaceConfigsQuery.data,
    isDiscoveryLoading: workspaceConfigsQuery.isLoading,
    currentValue: isReferencePointsHidden,
    queryClient,
    t,
    showOrvalErrorToast
  });

  // ignoreRef : passé uniquement par l'effet qui appelle cette fonction sans l'attendre (fire
  // and forget). Si l'effet se relance ou que le composant démonte avant la fin du PATCH, le
  // cleanup passe ignoreRef.current à true pour que cette réponse tardive n'écrase pas un état
  // plus récent. Les appels depuis les handlers (clic utilisateur) ne passent rien — pas de
  // risque de réponse concurrente à ignorer dans ce cas.
  const updateReferenceConfig = useCallback(
    async (enabled: boolean, id: number, silent = false, ignoreRef?: { current: boolean }) => {
      setIsUpdatingConfig(true);

      try {
        const response = await apiAdminworkspaceConfigsIdPatch(id.toString(), {
          value: enabled.toString()
        });

        if (ignoreRef?.current) return;

        if (response) {
          queryClient.invalidateQueries({ queryKey: ['workspaceConfigs'] });

          if (!silent) {
            toast.success(enabled ? t('toast.success.activated') : t('toast.success.deactivated'));
          }
        }
      } catch (error) {
        if (ignoreRef?.current) return;

        console.error('Error updating reference config:', error);

        if (isOrvalErrorFromPath(error, 'patch', '/admin/workspaceConfigs')) {
          showOrvalErrorToast(
            error,
            enabled ? t('toast.error.activation_failed') : t('toast.error.deactivation_failed')
          );
        } else {
          toast.error(
            enabled ? t('toast.error.activation_failed') : t('toast.error.deactivation_failed')
          );
        }

        setSwitchState(!enabled);
      } finally {
        setIsUpdatingConfig(false);
      }
    },
    [queryClient, t, showOrvalErrorToast]
  );

  const handleSwitchChange = (enabled: boolean) => {
    if (enabled && !switchState) {
      setShowActivationDialog(true);
    } else {
      setSwitchState(enabled);
      if (configId) {
        updateReferenceConfig(enabled, configId);
      }
    }
  };

  const handleConfirmActivation = () => {
    setShowActivationDialog(false);
    setSwitchState(true);
    if (configId) {
      updateReferenceConfig(true, configId);
    }
  };

  const handleCancelActivation = () => {
    setShowActivationDialog(false);
  };

  const handleShowReferenceChange = async (checked: boolean) => {
    const succeeded = await hideReferenceSwitch.toggle(!checked, { isActivated: checked });

    // Le référentiel masqué rend le masquage des montants inerte (403 back sur toute
    // la collection) : on le remet à false par défaut plutôt que de laisser une valeur
    // figée que l'utilisateur retrouverait active à la réactivation du switch parent.
    // On attend le succès du toggle principal avant ce 2e écriture : sinon, si le premier
    // échoue et revert, ce reset silencieux peut déjà être passé côté serveur et écraser
    // la préférence de l'utilisateur sans qu'il le sache.
    if (succeeded && !checked && hideAmountSwitch.value) {
      hideAmountSwitch.toggle(false, { isActivated: false, silent: true });
    }
  };

  const handleHideAmountChange = (checked: boolean) => {
    hideAmountSwitch.toggle(checked, { isActivated: checked });
  };

  // Trouve configId (workspace config is-reference-enabled) puis applique la règle métier :
  // un référentiel vide (plus aucun produit importé) ne peut pas rester actif, on le désactive
  // silencieusement. Un seul effet (pas deux qui se déclenchent en chaîne via configId) — la
  // sync visuelle du switch reste séparée ci-dessous pour ne pas réintroduire le flicker.
  useEffect(() => {
    // isSuccess et pas !isLoading : un GET /references en erreur (retry: 0) laisserait data
    // undefined, donc hasReferences à false, et désactiverait le référentiel en base.
    if (!referencesQuery.isSuccess) return;

    const ignoreRef = { current: false };

    const resolveConfigAndApplyRule = async () => {
      if (!configId) {
        if (!workspaceConfigsQuery.data) return;

        const id = findWorkspaceConfigId(CONFIG_KEY_IS_REFERENCE_ENABLED);
        if (id) setConfigId(id);
        // On s'arrête là : configId qui vient de changer relance cet effet tout seul (il est
        // dans les deps), qui appliquera la règle métier au prochain passage. Continuer ici
        // enverrait un 2e PATCH identique en plus de celui déclenché par la relance.
        return;
      }

      if (isReferenceEnabled && !hasReferences && !ignoreRef.current) {
        await updateReferenceConfig(false, configId, true, ignoreRef);
      }
    };

    resolveConfigAndApplyRule();

    return () => {
      ignoreRef.current = true;
    };
  }, [
    configId,
    workspaceConfigsQuery.data,
    referencesQuery.isSuccess,
    hasReferences,
    isReferenceEnabled,
    findWorkspaceConfigId,
    updateReferenceConfig
  ]);

  // Sync visuelle du switch depuis une source unique (le contexte), même pattern que les
  // switchs afficher/masquer plus bas — pas de calcul croisé avec une autre query ici.
  useEffect(() => {
    setSwitchState(isReferenceEnabled);
  }, [isReferenceEnabled]);

  const { table } = useDataTable({
    data: references,
    columns: useProductReferenceColumns(),
    pageCount: referencesPagination?.last ?? 1,
    filterFields: PRODUCT_REFERENCE_FILTER_FIELDS,
    enableAdvancedFilter: true,
    manualPaginate: true,
    persistence: {
      // Persiste pagination, tri et filtres en localStorage.
      key: productsReferencesTablePersistenceKey
    }
  });

  const { pageIndex, pageSize } = table.getState().pagination;

  // La pagination vit dans la table (persistée) : on la répercute sur les params de la requête.
  useEffect(() => {
    setApiPagination((current) =>
      current.pageIndex === pageIndex && current.pageSize === pageSize
        ? current
        : { pageIndex, pageSize }
    );
  }, [pageIndex, pageSize]);

  const referenceFields = Object.keys(referenceImportRowSchema.shape);

  const csv = unparse({
    fields: referenceFields,
    data: [
      ['Exemple Brand', 'Ligne Produit A', 'Produit Exemple 1', 'PROD-REF-001', 100, ['sport']],
      ['Exemple Brand', 'Ligne Produit B', 'Produit Exemple 2', 'PROD-REF-002', 80, ['premium']],
      [
        'Exemple Brand',
        'Ligne Produit C',
        'Produit Exemple 3',
        'PROD-REF-003',
        60,
        ['sport', 'premium']
      ]
    ]
  });

  const filename = sanitizedWorkspaceName
    ? `${sanitizedWorkspaceName}_product_references_import_example.csv`
    : 'product_references_import_example.csv';

  // Préfixe sans params : invalide toutes les pages en cache, pas seulement la courante.
  const invalidateReferences = () =>
    queryClient.invalidateQueries({ queryKey: getApiReferencesGetCollectionQueryKey() });

  const handleParse = async (files: File[]) => {
    if (files[0].size > 5 * 1024 * 1024) {
      // 5MB max
      toast.error(t('import.file.too.large'));
      return;
    }

    const toastId = toast.loading(t('import.process.analyzing'));

    try {
      const result = await onParse(files);

      if (result.isPreview) {
        toast.dismiss(toastId);
        setPreviewState(result.errors ?? [], files[0], result.analysis ?? null);
      } else if (result.success) {
        toast.success(result.message || t('import.process.success'), { id: toastId });
        resetState();
        invalidateReferences();
      } else if (result.errors && result.errors.length > 0) {
        toast.dismiss(toastId);
        setErrorState(result.errors, files[0]);
      }
    } catch (error) {
      showOrvalErrorToast(error, typeof error === 'string' ? error : t('import.process.error'), {
        id: toastId
      });
    }
  };

  const handleRetry = async () => {
    if (state.originalFile) {
      const toastId = toast.loading(t('import.process.loading'));

      try {
        const result = await onParse([state.originalFile], true);

        if (result.success) {
          toast.success(result.message || t('import.process.success'), { id: toastId });
          resetState();
          invalidateReferences();
        } else {
          toast.error(result.message || t('import.process.error'), { id: toastId });
          if (result.errors && result.errors.length > 0) {
            setErrorState(result.errors, state.originalFile);
          }
        }
      } catch (error) {
        showOrvalErrorToast(error, typeof error === 'string' ? error : t('import.process.error'), {
          id: toastId
        });
      }
    }
  };

  const exportData = async () => {
    setIsExporting(true);

    try {
      // Le tableau n'a que la page courante : l'export parcourt toutes les pages côté serveur.
      const data = await fetchAllReferences();

      const fields = referenceFields;

      const referencesData = data.map((reference: ReferenceReferenceReadModel) => [
        reference.brand,
        reference.productLine || '',
        reference.name,
        reference.uniqueCode,
        reference.pointValue,
        reference.tags ?? []
      ]);

      const csv = unparse({
        fields: fields,
        data: referencesData
      });

      const currentDate = new Date();
      const fileName = `product-references-export-${format(currentDate, 'yyyy-MM-dd-HHmmss')}.csv`;

      downloadCSV(csv, fileName);
    } catch {
      toast.error(t('export.error'));
    } finally {
      setIsExporting(false);
    }
  };

  if (state.isPreview && state.originalFile) {
    return (
      <ImportErrorPage
        errors={state.errors}
        originalFile={state.originalFile}
        onRetry={handleRetry}
        onNewFile={resetState}
        importType="references"
        analysis={state.analysis}
      />
    );
  }

  return (
    <div className="space-y-5">
      <h1 className="text-4xl font-bold">{t('admin.products.references.title')}</h1>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <h2 className="text-xl font-bold">{t('admin.products.references.section.title')}</h2>
            <p className="text-muted-foreground">
              {t('admin.products.references.section.description')}
            </p>
          </div>
        </CardContent>

        <CardContent className="space-y-6">
          <div className="flex items-center space-x-2">
            <Switch
              id="enable-product-references"
              checked={switchState}
              disabled={!configId || !hasReferences || isUpdatingConfig}
              onCheckedChange={handleSwitchChange}
            />

            <Label htmlFor="enable-product-references">
              {t('admin.products.references.enable.label')}
            </Label>
          </div>

          {switchState && (
            <div className="flex items-center space-x-2">
              <Switch
                id="show-product-references"
                checked={!hideReferenceSwitch.value}
                disabled={!hideReferenceSwitch.isReady || hideReferenceSwitch.isUpdating}
                onCheckedChange={handleShowReferenceChange}
              />

              <Label htmlFor="show-product-references">
                {t('admin.products.references.show.label')}
              </Label>
            </div>
          )}

          {switchState && !hideReferenceSwitch.value && (
            <div className="flex items-center space-x-2">
              <Switch
                id="hide-product-references-amount"
                checked={hideAmountSwitch.value}
                disabled={!hideAmountSwitch.isReady || hideAmountSwitch.isUpdating}
                onCheckedChange={handleHideAmountChange}
              />

              <Label htmlFor="hide-product-references-amount">
                {t('admin.products.references.hide.amount.label')}
              </Label>
            </div>
          )}

          {referencesQuery.isLoading ? (
            <DataTableSkeleton
              columnCount={6}
              cellWidths={['10rem', '15rem', '20rem', '12rem', '8rem', '4rem']}
            />
          ) : referencesQuery.isError ? (
            <div className="py-8 text-center">
              <p className="text-muted-foreground">
                {t('admin.products.references.error.loading')}
              </p>
            </div>
          ) : (
            <>
              {hasReferences && (
                <div className="flex justify-end mb-4">
                  <Button onClick={exportData} disabled={isExporting} rounded="full">
                    {isExporting ? (
                      <div className="mr-2 w-4 h-4 rounded-full border-2 border-current animate-spin border-t-transparent" />
                    ) : (
                      <Download className="mr-2 w-4 h-4" />
                    )}
                    {t('admin.products.references.export.button')}
                  </Button>
                </div>
              )}
              <DataTable
                table={table}
                totalItems={referencesPagination?.totalItems}
                pageSizeOptions={REFERENCES_PAGE_SIZE_OPTIONS}
              />
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <h2 className="text-xl font-bold">{t('admin.products.references.import.title')}</h2>
            <p className="text-muted-foreground">
              {t('admin.products.references.import.description')}
            </p>
          </div>
        </CardContent>

        <CardContent>
          <div className="space-y-4">
            <CsvFile csv={csv} filename={filename} label={t('common.csv.example.button')} />
          </div>

          <FileUploader
            accept={{
              'text/csv': [],
              'application/vnd.ms-excel': [],
              'application/csv': [],
              'text/x-csv': [],
              'application/x-csv': [],
              'text/comma-separated-values': [],
              'text/x-comma-separated-values': []
            }}
            onParse={handleParse}
          />
        </CardContent>
      </Card>

      <Dialog open={showActivationDialog} onOpenChange={setShowActivationDialog}>
        <DialogContent className="p-10 !rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {t('admin.products.references.activation.dialog.title')}
            </DialogTitle>
            <DialogDescription className="text-base">
              {t('admin.products.references.activation.dialog.description')}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex gap-3 pt-4">
            <Button variant="outline" onClick={handleCancelActivation} disabled={isUpdatingConfig}>
              {t('label.cancel')}
            </Button>
            <Button onClick={handleConfirmActivation} disabled={isUpdatingConfig}>
              {t('admin.products.references.activation.dialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
